import { ArtefactCitation, ArtefactCitationCoordinate, ArtefactCitationMetadata } from '../types';

const REQUIRED_NAVIGATION_FIELDS: Array<keyof ArtefactCitationCoordinate> = [
  'pageNumber',
  'startIndex',
  'x',
  'y',
  'textLength',
];

function parseJsonCoordinates(value: string) {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function normalizeMetadata(metadata: ArtefactCitationMetadata | null | undefined) {
  return metadata && typeof metadata === 'object' ? metadata : null;
}

function normalizeString(value: unknown) {
  return typeof value === 'string' ? value.trim() : '';
}

function hasCitationCoordinatesPayload(coordinates: ArtefactCitation['coordinates']) {
  if (coordinates == null) {
    return false;
  }

  if (typeof coordinates === 'string') {
    return coordinates.trim().length > 0;
  }

  if (Array.isArray(coordinates)) {
    return true;
  }

  return typeof coordinates === 'object';
}

function toFiniteNumber(value: unknown) {
  if (value == null) {
    return null;
  }

  const resolved = typeof value === 'string' ? Number(value) : value;
  return typeof resolved === 'number' && Number.isFinite(resolved) ? resolved : null;
}

function getCitationMetadata(citation: ArtefactCitation | null | undefined) {
  return normalizeMetadata(citation?.metadata);
}

export function normalizeCitationCoordinates(
  coordinates: ArtefactCitation['coordinates'],
): ArtefactCitationCoordinate[] {
  if (coordinates == null) {
    return [];
  }

  let resolved = coordinates;

  if (typeof resolved === 'string') {
    const trimmed = resolved.trim();
    if (!trimmed) {
      return [];
    }

    resolved = parseJsonCoordinates(trimmed);
  }

  if (Array.isArray(resolved)) {
    return resolved.filter(
      (coordinate): coordinate is ArtefactCitationCoordinate =>
        Boolean(coordinate) && typeof coordinate === 'object',
    );
  }

  if (resolved && typeof resolved === 'object') {
    return [resolved as ArtefactCitationCoordinate];
  }

  return [];
}

export function getCitationPageNumber(citation: ArtefactCitation | null | undefined) {
  if (!citation) {
    return null;
  }

  const directPageNumber = toFiniteNumber(citation.pageNumber);
  if (directPageNumber != null) {
    return directPageNumber;
  }

  const firstCoordinate = normalizeCitationCoordinates(citation.coordinates)[0];
  return toFiniteNumber(firstCoordinate?.pageNumber);
}

export function getCitationText(citation: ArtefactCitation | null | undefined) {
  if (!citation) {
    return '';
  }

  const metadata = getCitationMetadata(citation);
  const metadataText = normalizeString(metadata?.text);

  if (metadataText) {
    return metadataText;
  }

  const text = normalizeString(citation.text);
  if (text) {
    return text;
  }

  return normalizeString(citation.textExcerpt);
}

export function getCitationDocumentReference(citation: ArtefactCitation | null | undefined) {
  if (!citation) {
    return '';
  }

  return getCitationDocumentName(citation) || getCitationFileId(citation);
}

export function getCitationDocumentName(citation: ArtefactCitation | null | undefined) {
  if (!citation) {
    return '';
  }

  const metadata = getCitationMetadata(citation);
  return (
    normalizeString(citation.documentName) ||
    normalizeString(metadata?.documentName) ||
    normalizeString(metadata?.name) ||
    normalizeString(metadata?.fileName) ||
    ''
  );
}

export function getCitationDocumentReferenceLabel(citation: ArtefactCitation | null | undefined) {
  return getCitationDocumentName(citation) ? 'Document Name:' : 'Document Id:';
}

export function getCitationFileId(citation: ArtefactCitation | null | undefined) {
  if (!citation) {
    return '';
  }

  const metadata = getCitationMetadata(citation);
  return (
    normalizeString(citation.fileId) ||
    normalizeString(metadata?.fileId) ||
    normalizeString(metadata?.id)
  );
}

export function canRenderCitation(citation: ArtefactCitation | null | undefined) {
  if (!citation) {
    return false;
  }

  return (
    Boolean(getCitationDocumentReference(citation)) &&
    hasCitationCoordinatesPayload(citation.coordinates)
  );
}

export function isCitationNavigationEnabled(citation: ArtefactCitation | null | undefined) {
  if (!canRenderCitation(citation)) {
    return false;
  }

  const coordinates = normalizeCitationCoordinates(citation?.coordinates);
  if (coordinates.length === 0) {
    return false;
  }

  return coordinates.every((coordinate) =>
    REQUIRED_NAVIGATION_FIELDS.every((field) => toFiniteNumber(coordinate?.[field]) != null),
  );
}

export function getCitationNavigationLocation(citation: ArtefactCitation | null | undefined) {
  if (!isCitationNavigationEnabled(citation)) {
    return null;
  }

  const [coordinate] = normalizeCitationCoordinates(citation?.coordinates);
  if (!coordinate) {
    return null;
  }

  const pageNumber = toFiniteNumber(coordinate.pageNumber);
  const startIndex = toFiniteNumber(coordinate.startIndex);
  const x = toFiniteNumber(coordinate.x);
  const y = toFiniteNumber(coordinate.y);
  const textLength = toFiniteNumber(coordinate.textLength);

  if (
    pageNumber == null ||
    startIndex == null ||
    x == null ||
    y == null ||
    textLength == null
  ) {
    return null;
  }

  return {
    pageNumber,
    startIndex,
    x,
    y,
    textLength,
  };
}

export function getArtefactCitationSignature(citation: ArtefactCitation | null | undefined) {
  if (!citation) {
    return 'citation:null';
  }

  const metadata = getCitationMetadata(citation);

  return JSON.stringify({
    type: citation.type ?? null,
    fileId: citation.fileId ?? metadata?.fileId ?? metadata?.id ?? null,
    sequence: citation.sequence ?? null,
    documentName: getCitationDocumentReference(citation) || null,
    text: getCitationText(citation),
    pageNumber: getCitationPageNumber(citation),
    coordinates: normalizeCitationCoordinates(citation.coordinates),
  });
}

export function mergeArtefactCitations(
  citations: Array<ArtefactCitation | null | undefined> | null | undefined,
) {
  const merged: ArtefactCitation[] = [];
  const seen = new Set<string>();

  (citations || []).forEach((citation) => {
    if (!citation) {
      return;
    }

    const signature = getArtefactCitationSignature(citation);
    if (seen.has(signature)) {
      return;
    }

    seen.add(signature);
    merged.push(citation);
  });

  return merged;
}
